// public/js/modules/map/history.js
import {
  currentRouteLayers,
  setCurrentRouteLayers,
  currentRouteSourceIds,
  setCurrentRouteSourceIds,
  currentOpenPopup,
  setCurrentOpenPopup,
  markers,
  currentFilteredAssets,
  currentHistoryPointMarkers,
  setCurrentHistoryPointMarkers,
  setCurrentHistoryPoints
} from './state.js';
import { mapFilterManager } from '../filters/filter-manager.js';
import { formatAssetLabel, formatLocalTime } from '../../utils/asset-utils.js';
import { simplifyRoute, collapseDwells, findStopIndices, SIMPLIFICATION } from './simplify.js';
import { LABEL_FONT } from './constants.js';
import { drawAccuracy, clearAccuracy } from './accuracy.js';
import { convertValue } from '../../utils/units.js';

// Read the GPS accuracy (metres) from a telemetry/asset point, tolerant of field names.
const accuracyOf = (p) => Number(p?.accuracy ?? p?.posAcc ?? p?.lastAccuracy);

const ROUTE_BASE = 'history-route-base';
const ROUTE_RECENT = 'history-route-recent';
const ROUTE_ARROWS = 'history-route-arrows';

// Wide, transparent hit-area line — makes the thin visible route easy to click.
const ROUTE_HIT = 'history-route-hit';
const ROUTE_SOURCE_HIT = `${ROUTE_HIT}-src`;
const ROUTE_LAYER_HIT = `${ROUTE_HIT}-layer`;

const ROUTE_SOURCE_BASE = `${ROUTE_BASE}-src`;
const ROUTE_SOURCE_RECENT = `${ROUTE_RECENT}-src`;
const ROUTE_LAYER_BASE = `${ROUTE_BASE}-layer`;
const ROUTE_LAYER_RECENT = `${ROUTE_RECENT}-layer`;
const ROUTE_LAYER_ARROWS = `${ROUTE_ARROWS}-layer`;

// Current route points (replaced on each load). Used by the single delegated
// click handler so we never stack per-layer handlers with stale closures.
let currentRoutePoints = [];

// Accumulated positional points for the asset whose path is currently drawn.
// The default 14-day load seeds it; loading extra history windows (from the map
// info-panel picker) APPENDS to it — deduped so overlapping windows count each
// point once. Reset when a different asset's route is loaded.
let routeAssetId = null;
let accumRaw = [];
const accumKeys = new Set();
// Parallel accumulation of ALL raw rows for the same asset, INCLUDING sensor-only
// heartbeats without a GPS fix. Used by the trail-events feature so threshold
// events cover extra loaded history windows too, not just the sidebar's 14 days.
let accumAllRaw = [];
const accumAllKeys = new Set();
const ptKey = (p) => `${p.timestamp}#${p.deviceId || ''}`;
function resetAccum(assetUUID) {
  routeAssetId = assetUUID;
  accumRaw = []; accumKeys.clear();
  accumAllRaw = []; accumAllKeys.clear();
}
function mergeAccum(pts) {
  for (const p of (pts || [])) {
    const k = ptKey(p);
    if (!accumKeys.has(k)) { accumKeys.add(k); accumRaw.push(p); }
  }
  accumRaw.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
}
function mergeAllRaw(pts) {
  for (const p of (pts || [])) {
    const k = ptKey(p);
    if (!accumAllKeys.has(k)) { accumAllKeys.add(k); accumAllRaw.push(p); }
  }
  accumAllRaw.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
}

// Guard against overlapping async loads/redraws: only the latest sequence may
// touch the map. Fixes the "path appears briefly then vanishes" race where a
// stale load's clear/draw landed after a newer one.
let loadSeq = 0;

// ── Trail age gradient (blue = old → red = recent) ─────────────────────────
// MapLibre renders `line-gradient` on the GPU (lineMetrics source), so unlike
// the old per-segment approach this costs nothing at runtime.
const GRADIENT_LS_KEY = 'map.trailGradient';
function safeLS(key) { try { return localStorage.getItem(key); } catch (e) { return null; } }
let gradientEnabled = safeLS(GRADIENT_LS_KEY) !== 'off'; // default ON

export function isTrailGradientEnabled() { return gradientEnabled; }

export function setTrailGradientEnabled(map, on) {
  gradientEnabled = !!on;
  try { localStorage.setItem(GRADIENT_LS_KEY, gradientEnabled ? 'on' : 'off'); } catch (e) {}
  // Re-render the active route in the newly chosen scheme (no refetch).
  redrawActiveRoute(map);
}

const GRAD_OLD = [29, 78, 216];    // #1d4ed8 (blue — oldest)
const GRAD_NEW = [220, 38, 38];    // #dc2626 (red — newest)

function gradientColorAt(frac) {
  const f = Math.min(1, Math.max(0, frac));
  const c = GRAD_OLD.map((o, i) => Math.round(o + (GRAD_NEW[i] - o) * f));
  return `rgb(${c[0]},${c[1]},${c[2]})`;
}

// Build a ['interpolate', …, ['line-progress'], …] expression that maps the
// line's distance progress to the POINT TIME at each vertex, so the colour
// encodes age even when the asset moved at very different speeds.
// Returns null when a gradient is not applicable (degenerate line).
function buildAgeGradientExpression(simplified, coords) {
  if (!simplified || simplified.length < 2 || coords.length !== simplified.length) return null;

  // Cumulative distance (equirectangular approx is fine for colour stops).
  const cum = [0];
  let total = 0;
  for (let i = 1; i < coords.length; i++) {
    const [x1, y1] = coords[i - 1];
    const [x2, y2] = coords[i];
    const ky = 111320;
    const kx = ky * Math.cos(((y1 + y2) / 2) * Math.PI / 180);
    total += Math.hypot((x2 - x1) * kx, (y2 - y1) * ky);
    cum.push(total);
  }
  if (!(total > 0)) return null;

  const t0 = new Date(simplified[0].timestamp).getTime();
  const t1 = new Date(simplified[simplified.length - 1].timestamp).getTime();
  const span = Math.max(1, t1 - t0);

  // Sample up to ~40 stops; stops must be strictly ascending in progress.
  const expr = ['interpolate', ['linear'], ['line-progress']];
  const step = Math.max(1, Math.floor(coords.length / 40));
  let lastProg = -1;
  const pushStop = (i) => {
    const prog = i === coords.length - 1 ? 1 : cum[i] / total;
    if (prog <= lastProg + 1e-6 && prog !== 1) return;
    if (prog === 1 && lastProg >= 1) return;
    lastProg = prog;
    const tf = (new Date(simplified[i].timestamp).getTime() - t0) / span;
    expr.push(prog, gradientColorAt(tf));
  };
  pushStop(0);
  for (let i = step; i < coords.length - 1; i += step) pushStop(i);
  pushStop(coords.length - 1);
  return expr.length >= 7 ? expr : null;  // needs at least 2 stops
}

// ── Record mode: keep ("freeze") previous assets' trails on the map ─────────
const FROZEN_PALETTE = [
  '#8b5cf6', '#f97316', '#10b981', '#e11d48', '#0ea5e9',
  '#eab308', '#84cc16', '#d946ef', '#14b8a6', '#64748b'
];
let recordMode = false;
let frozenColorCursor = 0;
// assetUUID → { rawPoints, allRaw, simplified, color, label, layerIds, sourceIds, clickHandler, enterHandler, leaveHandler }
const frozenTrails = new Map();

export function isRecordMode() { return recordMode; }

export function setRecordMode(map, on) {
  recordMode = !!on;
  if (!recordMode) {
    // Toggle off = tidy map: drop every frozen trail immediately.
    for (const uuid of [...frozenTrails.keys()]) removeFrozenTrail(map, uuid);
    frozenColorCursor = 0;
  }
}

function frozenIds(uuid) {
  return {
    lineSrc: `frozen-trail-${uuid}-src`,
    lineLayer: `frozen-trail-${uuid}-layer`,
    hitSrc: `frozen-trail-${uuid}-hit-src`,
    hitLayer: `frozen-trail-${uuid}-hit-layer`,
  };
}

function drawFrozenTrail(map, entry) {
  const ids = frozenIds(entry.assetUUID);
  const coords = entry.simplified.map(p => [parseFloat(p.lon), parseFloat(p.lat)]);
  if (coords.length < 2) return;

  const addLine = (srcId, layerId, paint) => {
    try { if (map.getLayer(layerId)) map.removeLayer(layerId); } catch (e) {}
    try { if (map.getSource(srcId)) map.removeSource(srcId); } catch (e) {}
    map.addSource(srcId, {
      type: 'geojson',
      data: { type: 'Feature', geometry: { type: 'LineString', coordinates: coords }, properties: {} }
    });
    map.addLayer({
      id: layerId, type: 'line', source: srcId,
      layout: { 'line-join': 'round', 'line-cap': 'round' },
      paint
    });
  };

  addLine(ids.lineSrc, ids.lineLayer, { 'line-color': entry.color, 'line-opacity': 0.75, 'line-width': 4 });
  addLine(ids.hitSrc, ids.hitLayer, { 'line-color': '#000', 'line-opacity': 0, 'line-width': 16 });

  entry.layerIds = [ids.lineLayer, ids.hitLayer];
  entry.sourceIds = [ids.lineSrc, ids.hitSrc];

  if (!entry.clickHandler) {
    entry.clickHandler = (e) => showFrozenPointPopup(map, entry, e.lngLat);
    entry.enterHandler = () => { map.getCanvas().style.cursor = 'pointer'; };
    entry.leaveHandler = () => { map.getCanvas().style.cursor = ''; };
    map.on('click', ids.hitLayer, entry.clickHandler);
    map.on('mouseenter', ids.hitLayer, entry.enterHandler);
    map.on('mouseleave', ids.hitLayer, entry.leaveHandler);
  }
}

function removeFrozenTrail(map, uuid) {
  const entry = frozenTrails.get(uuid);
  if (!entry) return;
  const ids = frozenIds(uuid);
  if (map) {
    (entry.layerIds || []).forEach(id => { try { if (map.getLayer(id)) map.removeLayer(id); } catch (e) {} });
    (entry.sourceIds || []).forEach(id => { try { if (map.getSource(id)) map.removeSource(id); } catch (e) {} });
    try {
      if (entry.clickHandler) map.off('click', ids.hitLayer, entry.clickHandler);
      if (entry.enterHandler) map.off('mouseenter', ids.hitLayer, entry.enterHandler);
      if (entry.leaveHandler) map.off('mouseleave', ids.hitLayer, entry.leaveHandler);
    } catch (e) {}
  }
  frozenTrails.delete(uuid);
}

function showFrozenPointPopup(map, entry, lngLat) {
  const pts = entry.simplified || [];
  if (!pts.length) return;
  let best = null, bestD = Infinity;
  pts.forEach((p, i) => {
    const dx = parseFloat(p.lon) - lngLat.lng;
    const dy = parseFloat(p.lat) - lngLat.lat;
    const d = dx * dx + dy * dy;
    if (d < bestD) { bestD = d; best = { ...p, _idx: i }; }
  });
  if (!best) return;
  if (currentOpenPopup) { try { currentOpenPopup.remove(); } catch (e) {} }
  const header = `<div style="display:flex;align-items:center;gap:6px;margin-bottom:2px;">
      <span style="width:10px;height:10px;border-radius:3px;background:${entry.color};display:inline-block;"></span>
      <strong>${escapeHtmlLite(entry.label)}</strong></div>`;
  const popup = new maplibregl.Popup({ closeOnClick: true, offset: 12 })
    .setLngLat([parseFloat(best.lon), parseFloat(best.lat)])
    .setHTML(header + buildPointInfoHTML(best, pts.length))
    .addTo(map);
  setCurrentOpenPopup(popup);
}

function escapeHtmlLite(s) {
  return String(s ?? '').replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
}

// Freeze the currently drawn route into the frozen-trails registry (record mode).
function freezeCurrentTrail(map) {
  if (!routeAssetId || currentRoutePoints.length < 2) return;
  // Re-freezing the same asset replaces its previous frozen copy.
  if (frozenTrails.has(routeAssetId)) removeFrozenTrail(map, routeAssetId);

  const asset = currentFilteredAssets.find(a => a.assetUUID === routeAssetId);
  const entry = {
    assetUUID: routeAssetId,
    rawPoints: accumRaw.slice(),
    allRaw: accumAllRaw.slice(),
    simplified: currentRoutePoints.slice(),
    color: FROZEN_PALETTE[frozenColorCursor++ % FROZEN_PALETTE.length],
    label: asset ? formatAssetLabel(asset) : routeAssetId,
    layerIds: [], sourceIds: [],
    clickHandler: null, enterHandler: null, leaveHandler: null,
  };
  frozenTrails.set(routeAssetId, entry);
  drawFrozenTrail(map, entry);
}

// Redraw all frozen trails (after a base-style switch wiped their layers).
export function redrawFrozenTrails(map) {
  if (!map) return;
  frozenTrails.forEach(entry => { try { drawFrozenTrail(map, entry); } catch (e) {} });
}

function showNearestPointPopup(map, lngLat) {
  if (!currentRoutePoints.length) return;

  let best = null;
  let bestD = Infinity;
  currentRoutePoints.forEach((p, i) => {
    const dx = parseFloat(p.lon) - lngLat.lng;
    const dy = parseFloat(p.lat) - lngLat.lat;
    const d = dx * dx + dy * dy;
    if (d < bestD) { bestD = d; best = { ...p, _idx: i }; }
  });
  if (!best) return;

  if (currentOpenPopup) { try { currentOpenPopup.remove(); } catch (e) {} }

  const popup = new maplibregl.Popup({ closeOnClick: true, offset: 12 })
    .setLngLat([parseFloat(best.lon), parseFloat(best.lat)])
    .setHTML(buildPointInfoHTML(best, currentRoutePoints.length))
    .addTo(map);

  setCurrentOpenPopup(popup);
}

// Attach route click/hover handlers exactly once per map instance, bound to the
// stable wide hit-layer id. Avoids the per-load handler accumulation and stale
// closures, and the wide hit area makes a slightly-off click still register.
function attachRouteInteractions(map) {
  if (map.__mapRouteHandlers) return;
  map.on('click', ROUTE_LAYER_HIT, (e) => showNearestPointPopup(map, e.lngLat));
  map.on('mouseenter', ROUTE_LAYER_HIT, () => { map.getCanvas().style.cursor = 'pointer'; });
  map.on('mouseleave', ROUTE_LAYER_HIT, () => { map.getCanvas().style.cursor = ''; });
  map.__mapRouteHandlers = true;
}

// Remove the drawn route visuals (layers/sources/markers/accuracy) but KEEP the
// accumulated points, so a redraw from the accumulation starts from a clean map.
function removeRouteVisuals(map) {
  if (!map) return;

  // Always remove LAYERS before SOURCES (MapLibre rule).
  const allLayerIds = new Set([
    ...(currentRouteLayers || []),
    ROUTE_LAYER_HIT,
    ROUTE_LAYER_ARROWS,
    ROUTE_LAYER_RECENT,
    ROUTE_LAYER_BASE
  ]);

  const allSourceIds = new Set([
    ...(currentRouteSourceIds || []),
    ROUTE_SOURCE_HIT,
    ROUTE_SOURCE_RECENT,
    ROUTE_SOURCE_BASE,
    `${ROUTE_ARROWS}-src`
  ]);

  allLayerIds.forEach(id => { try { if (map.getLayer(id)) map.removeLayer(id); } catch (e) {} });
  allSourceIds.forEach(id => { try { if (map.getSource(id)) map.removeSource(id); } catch (e) {} });

  (currentHistoryPointMarkers || []).forEach(m => { try { m.remove(); } catch (e) {} });
  setCurrentHistoryPointMarkers([]);

  clearAccuracy(map);
  setCurrentRouteLayers([]);
  setCurrentRouteSourceIds([]);
  currentRoutePoints = [];
}

// Full clear: also drops the sidebar's history points and resets the point
// accumulation (used when closing the panel or switching to another asset).
// In record mode the current trail is FROZEN instead of lost — unless this
// clear is part of reloading the very same asset (opts.reloadOf).
export function clearRoute(map, opts = {}) {
  loadSeq++;   // invalidate any in-flight load/draw
  if (recordMode && routeAssetId && routeAssetId !== opts.reloadOf) {
    try { freezeCurrentTrail(map); } catch (e) { console.warn('[map] freeze trail failed', e); }
  }
  removeRouteVisuals(map);
  setCurrentHistoryPoints([]);
  resetAccum(null);
  // Trail-dependent overlays (event pins) listen for this.
  window.dispatchEvent(new CustomEvent('routeCleared'));
}

function makeStopMarker(map, lngLat, color) {
  const el = document.createElement('div');
  el.style.cssText = `width:11px;height:11px;background:${color};border:2px solid #fff;border-radius:50%;box-shadow:0 0 0 1px rgba(0,0,0,0.2);`;
  return new maplibregl.Marker({ element: el, anchor: 'center' })
    .setLngLat(lngLat)
    .addTo(map);
}

// Detect trip start/end events among the route points.
// Prefers the device-reported reason (1 = Start of trip, 2 = End of trip);
// falls back to inTrip transitions for data without a reason code.
function detectTripEvents(points) {
  const events = [];
  let prevInTrip = null;
  for (let i = 0; i < points.length; i++) {
    const p = points[i];
    let type = null;

    if (p.recordReason === 1) type = 'start';
    else if (p.recordReason === 2) type = 'end';
    else if (p.inTrip != null) {
      if (prevInTrip === false && p.inTrip === true) type = 'start';
      else if (prevInTrip === true && p.inTrip === false) type = 'end';
    }

    if (type) events.push({ type, point: p });
    if (p.inTrip != null) prevInTrip = p.inTrip;
  }
  return events;
}

// A dwell shorter than this is drawn as a plain point on the line, not a marker:
// an engine stand in daily use pauses for an hour many times a day, and a clock
// at each of them is the scribble back in a different costume. Two hours is a
// parking spot.
const DWELL_MARKER_MIN_MS = 2 * 60 * 60 * 1000;
const dwellMs = (pt) => new Date(pt.dwell.to) - new Date(pt.dwell.from);

function formatDwellDuration(ms) {
  const m = Math.round(ms / 60000);
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60), d = Math.floor(h / 24);
  if (d >= 1) return `${d} d ${h - d * 24} h`;
  return `${h} h ${m - h * 60} min`;
}

function makeDwellMarker(map, lngLat, point) {
  const el = document.createElement('div');
  el.style.cssText = `width:22px;height:22px;border-radius:50%;background:#64748b;` +
    `border:2px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,0.4);display:flex;` +
    `align-items:center;justify-content:center;cursor:pointer;`;
  el.innerHTML = `<i class="fa-solid fa-clock" style="color:#fff;font-size:10px;"></i>`;
  el.title = `Parked ${formatDwellDuration(dwellMs(point))}`;

  const marker = new maplibregl.Marker({ element: el, anchor: 'center' })
    .setLngLat(lngLat)
    .addTo(map);

  el.addEventListener('click', (ev) => {
    ev.stopPropagation();
    if (currentOpenPopup) { try { currentOpenPopup.remove(); } catch (e) {} }
    const html = `
      <div style="font-size:0.9rem; min-width:200px;">
        <strong><i class="fa-solid fa-clock me-1"></i>Parked ${formatDwellDuration(dwellMs(point))}</strong><br>
        ${formatLocalTime(point.dwell.from)} &rarr; ${formatLocalTime(point.dwell.to)}<br>
        <span class="text-muted" style="font-size:0.8rem;">${point.dwell.count} reports from this spot, shown as one</span>
      </div>`;
    const popup = new maplibregl.Popup({ closeOnClick: true, offset: 14 })
      .setLngLat(lngLat)
      .setHTML(html)
      .addTo(map);
    setCurrentOpenPopup(popup);
  });

  return marker;
}

function makeTripMarker(map, lngLat, type, point) {
  const isStart = type === 'start';
  const color = isStart ? '#16a34a' : '#dc2626';
  const icon = isStart ? 'fa-play' : 'fa-flag-checkered';

  const el = document.createElement('div');
  el.style.cssText = `width:22px;height:22px;border-radius:50%;background:${color};` +
    `border:2px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,0.4);display:flex;` +
    `align-items:center;justify-content:center;cursor:pointer;`;
  el.innerHTML = `<i class="fa-solid ${icon}" style="color:#fff;font-size:10px;"></i>`;
  el.title = isStart ? 'Trip start' : 'Trip end';

  const marker = new maplibregl.Marker({ element: el, anchor: 'center' })
    .setLngLat(lngLat)
    .addTo(map);

  el.addEventListener('click', (ev) => {
    ev.stopPropagation();
    if (currentOpenPopup) { try { currentOpenPopup.remove(); } catch (e) {} }
    const html = `
      <div style="font-size:0.9rem; min-width:180px;">
        <strong>${isStart ? '▶ Trip start' : '🏁 Trip end'}</strong><br>
        ${formatLocalTime(point.timestamp)}
        ${point.logReason ? `<br><span class="text-muted" style="font-size:0.8rem;">${point.logReason}</span>` : ''}
      </div>`;
    const popup = new maplibregl.Popup({ closeOnClick: true, offset: 14 })
      .setLngLat(lngLat)
      .setHTML(html)
      .addTo(map);
    setCurrentOpenPopup(popup);
  });

  return marker;
}

function buildPointInfoHTML(p, total) {
  const speed = p.speed != null ? convertValue(parseFloat(p.speed), 'km/h', 1) : null;
  const speedStr = speed ? `<br>Speed: <strong>${speed.value} ${speed.unit}</strong>` : '';
  const headingStr = p.heading != null ? `<br>Heading: ${Math.round(p.heading)}°` : '';
  const acc = accuracyOf(p);
  const accConv = Number.isFinite(acc) && acc > 0 ? convertValue(acc, 'm', 0) : null;
  const accStr = accConv ? `<br>Accuracy: ±${accConv.value} ${accConv.unit}` : '';
  return `
    <div style="font-size:0.9rem; min-width:220px; max-width:260px;">
      <strong>Point ${p._idx + 1} / ${total}</strong><br>
      ${formatLocalTime(p.timestamp)}${speedStr}${headingStr}${accStr}<br>
      <span class="text-muted" style="font-size:0.8rem;">${parseFloat(p.lat).toFixed(5)}, ${parseFloat(p.lon).toFixed(5)}</span>
    </div>
  `;
}

export async function loadAssetHistoryAndDrawRoute(assetUUID, mapInstance, opts = {}) {
  if (!mapInstance || !assetUUID) return;

  // Clear previous route + popups. Passing reloadOf keeps a same-asset reload
  // from freezing its own trail in record mode; a DIFFERENT asset's trail is
  // frozen here (record mode) or dropped (normal mode).
  clearRoute(mapInstance, { reloadOf: assetUUID });
  const seq = ++loadSeq;
  // Do NOT unconditionally closeCurrentPopup here — asset info popups should stay open
  // when clicking a marker and loading its history. Route-point popups are handled in their own click handlers.

  window.currentViewedHistoryAssetId = assetUUID;

  // Immediate feedback: draw the current-position accuracy circle from the
  // asset's stored lastAccuracy (works even if there is no route history).
  const assetForAcc = currentFilteredAssets.find(a => a.assetUUID === assetUUID);
  const curAcc = accuracyOf(assetForAcc);
  if (assetForAcc && Number.isFinite(curAcc) && curAcc > 0 &&
      Number.isFinite(+assetForAcc.lastLon) && Number.isFinite(+assetForAcc.lastLat)) {
    drawAccuracy(mapInstance, { circles: [{ lon: +assetForAcc.lastLon, lat: +assetForAcc.lastLat, radius: curAcc }] });
  }

  try {
    const since = new Date(Date.now() - 14 * 24 * 60 * 60 * 1000).toISOString();
    const url = `api/v1/assets/${encodeURIComponent(assetUUID)}/history?since=${encodeURIComponent(since)}`;

    const res = await window.authenticatedFetch(url);
    if (seq !== loadSeq) return;   // superseded while fetching
    if (!res.ok) {
      console.warn('[map] History load failed:', res.status);
      return 'error';
    }

    const data = await res.json();
    if (seq !== loadSeq) return;
    let rawPoints = (data.history || []).filter(p => p.lat != null && p.lon != null);

    if (rawPoints.length === 0) {
      window.showToast?.('No data for the last 14 days', 'info');
      return 'empty';
    }

    rawPoints.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));

    const serverSimplified = data.originalCount != null;
    const serverConfig = data.simplifyConfig || {};

    // The server's simplification keeps the parking scribble for the same
    // reason the client's did (see collapseDwells), so the collapse runs here
    // regardless of which side simplified.
    const simplified = serverSimplified
      ? collapseDwells(rawPoints)
      : simplifyRoute(rawPoints, {
          toleranceMeters: serverConfig.toleranceMeters,
          turnThresholdDeg: serverConfig.turnThresholdDeg,
          stopThresholdMs: serverConfig.stopThresholdMs,
        });

    const originalCount = data.originalCount ?? rawPoints.length;

    // Seed the accumulation for this asset; extra history windows (from the map
    // info-panel picker) append to it and re-draw via the shared renderer.
    resetAccum(assetUUID);
    mergeAllRaw(data.history || []);

    // Re-opening an asset that is frozen in record mode: take its trail back
    // over (including any extra windows it had) and drop the frozen copy.
    const frozen = frozenTrails.get(assetUUID);
    let drawPts = simplified;
    if (frozen) {
      removeFrozenTrail(mapInstance, assetUUID);
      mergeAccum(frozen.rawPoints);
      mergeAllRaw(frozen.allRaw);
      mergeAccum(simplified);
      drawPts = simplifyRoute(accumRaw, {});
    } else {
      mergeAccum(simplified);
    }

    const ptStr = drawPts.length < originalCount
      ? `${drawPts.length} pts (of ${originalCount})`
      : `${drawPts.length} pts`;

    drawRouteFromSimplified(mapInstance, drawPts, {
      assetUUID, fit: opts.fit !== false, toastText: `14-Day-History: ${ptStr}`,
    });
    return 'ok';
  } catch (err) {
    console.error('[map] loadAssetHistoryAndDrawRoute error', err);
    window.showToast?.('Error when loading route', 'danger');
    return 'error';
  }
}

// Draw the whole route from a set of (already-simplified) points. Extracted so the
// default 14-day load AND appendHistoryWindow render identically. Clears the
// previous visuals first but KEEPS the accumulation.
function drawRouteFromSimplified(mapInstance, simplified, { assetUUID, fit = true, toastText = null } = {}) {
  removeRouteVisuals(mapInstance);
  const coords = simplified.map(p => [parseFloat(p.lon), parseFloat(p.lat)]); // [lng, lat]

  // Expose the points to the delegated route click handler + the chart sidebar.
  currentRoutePoints = simplified;
  setCurrentHistoryPoints(simplified);
  window.dispatchEvent(new CustomEvent('assetHistoryLoaded', { detail: { assetUUID } }));

  const accCircles = simplified
    .map(p => ({ lon: parseFloat(p.lon), lat: parseFloat(p.lat), radius: accuracyOf(p) }))
    .filter(c => Number.isFinite(c.lon) && Number.isFinite(c.lat) && Number.isFinite(c.radius) && c.radius > 0);
  if (accCircles.length) drawAccuracy(mapInstance, { circles: accCircles, path: accCircles });

  const recentCutoff = Date.now() - SIMPLIFICATION.RECENT_PATH_HOURS * 60 * 60 * 1000;
  const recentSimplified = simplified.filter(p => new Date(p.timestamp).getTime() >= recentCutoff);
  const hasRecentSubset = recentSimplified.length >= 2 && recentSimplified.length < simplified.length;

  const addedLayerIds = [];
  const addedSourceIds = [];

  const addLineSourceAndLayer = (baseId, coordinates, color, opacity, weight, gradientExpr = null) => {
    const srcId = `${baseId}-src`;
    const layerId = `${baseId}-layer`;
    try { if (mapInstance.getLayer(layerId)) mapInstance.removeLayer(layerId); } catch (e) {}
    try { if (mapInstance.getSource(srcId)) mapInstance.removeSource(srcId); } catch (e) {}
    mapInstance.addSource(srcId, {
      type: 'geojson',
      lineMetrics: !!gradientExpr,   // required for line-gradient
      data: { type: 'Feature', geometry: { type: 'LineString', coordinates }, properties: {} }
    });
    addedSourceIds.push(srcId);
    const paint = { 'line-color': color, 'line-opacity': opacity, 'line-width': weight };
    if (gradientExpr) paint['line-gradient'] = gradientExpr;
    mapInstance.addLayer({
      id: layerId, type: 'line', source: srcId,
      layout: { 'line-join': 'round', 'line-cap': 'round' },
      paint
    });
    addedLayerIds.push(layerId);
  };

  const gradientExpr = gradientEnabled ? buildAgeGradientExpression(simplified, coords) : null;

  if (gradientExpr) {
    // Age gradient: one line, colour encodes old (blue) → recent (red).
    addLineSourceAndLayer(ROUTE_BASE, coords, '#3b82f6', 0.9, 5, gradientExpr);
  } else if (hasRecentSubset) {
    addLineSourceAndLayer(ROUTE_BASE, coords, '#3b82f6', 0.38, 3.5);
    const recentCoords = recentSimplified.map(p => [parseFloat(p.lon), parseFloat(p.lat)]);
    addLineSourceAndLayer(ROUTE_RECENT, recentCoords, '#06b6d4', 0.9, 5);
  } else {
    const isAllRecent = recentSimplified.length === simplified.length;
    const color = isAllRecent ? '#06b6d4' : '#3b82f6';
    const opacity = isAllRecent ? 0.88 : 0.78;
    addLineSourceAndLayer(ROUTE_BASE, coords, color, opacity, 5);
  }

  // Wide transparent hit area so clicking the thin route is reliable.
  if (coords.length >= 2) {
    try { if (mapInstance.getLayer(ROUTE_LAYER_HIT)) mapInstance.removeLayer(ROUTE_LAYER_HIT); } catch (e) {}
    try { if (mapInstance.getSource(ROUTE_SOURCE_HIT)) mapInstance.removeSource(ROUTE_SOURCE_HIT); } catch (e) {}
    mapInstance.addSource(ROUTE_SOURCE_HIT, {
      type: 'geojson',
      data: { type: 'Feature', geometry: { type: 'LineString', coordinates: coords }, properties: {} }
    });
    addedSourceIds.push(ROUTE_SOURCE_HIT);
    mapInstance.addLayer({
      id: ROUTE_LAYER_HIT, type: 'line', source: ROUTE_SOURCE_HIT,
      layout: { 'line-join': 'round', 'line-cap': 'round' },
      paint: { 'line-color': '#000000', 'line-opacity': 0, 'line-width': 18 }
    });
    addedLayerIds.push(ROUTE_LAYER_HIT);
    attachRouteInteractions(mapInstance);
  }

  // Directional arrows along the path.
  try {
    const arrowPoints = [];
    for (let i = 0; i < coords.length - 1; i += Math.max(1, Math.floor(coords.length / 12))) {
      const a = coords[i]; const b = coords[i + 1];
      const dx = b[0] - a[0]; const dy = b[1] - a[1];
      const bearing = (Math.atan2(dx, dy) * 180 / Math.PI + 360) % 360;
      arrowPoints.push({
        type: 'Feature',
        geometry: { type: 'Point', coordinates: [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2] },
        properties: { bearing: bearing - 90 }
      });
    }
    if (arrowPoints.length > 0) {
      const arrowSrcId = `${ROUTE_ARROWS}-src`;
      try { if (mapInstance.getLayer(ROUTE_LAYER_ARROWS)) mapInstance.removeLayer(ROUTE_LAYER_ARROWS); } catch (e) {}
      try { if (mapInstance.getSource(arrowSrcId)) mapInstance.removeSource(arrowSrcId); } catch (e) {}
      mapInstance.addSource(arrowSrcId, { type: 'geojson', data: { type: 'FeatureCollection', features: arrowPoints } });
      addedSourceIds.push(arrowSrcId);
      mapInstance.addLayer({
        id: ROUTE_LAYER_ARROWS, type: 'symbol', source: arrowSrcId,
        layout: {
          'text-field': '▶', 'text-font': LABEL_FONT, 'text-size': 11,
          'text-rotate': ['get', 'bearing'], 'text-allow-overlap': true, 'text-ignore-placement': true
        },
        paint: { 'text-color': '#ffffff', 'text-opacity': 0.85 }
      });
      addedLayerIds.push(ROUTE_LAYER_ARROWS);
    }
  } catch (arrowErr) {
    console.warn('[map] arrow symbols failed (non-fatal)', arrowErr);
  }

  setCurrentRouteLayers(addedLayerIds);
  setCurrentRouteSourceIds(addedSourceIds);

  // Key markers: start (green) + dwells (clock) + stops (orange) + trip events.
  // A dwell gets its own marker with the duration and report count; the plain
  // orange stop dot is for gaps that are NOT a collapsed parking spot.
  const keyMarkers = [];
  if (coords.length > 0) keyMarkers.push(makeStopMarker(mapInstance, coords[0], '#22c55e'));
  simplified.forEach(pt => {
    if (pt.dwell && dwellMs(pt) >= DWELL_MARKER_MIN_MS) {
      keyMarkers.push(makeDwellMarker(mapInstance, [parseFloat(pt.lon), parseFloat(pt.lat)], pt));
    }
  });
  findStopIndices(simplified).forEach(i => {
    const pt = simplified[i];
    if (pt.dwell) return;
    keyMarkers.push(makeStopMarker(mapInstance, [parseFloat(pt.lon), parseFloat(pt.lat)], '#f97316'));
  });
  detectTripEvents(simplified).forEach(ev => {
    keyMarkers.push(makeTripMarker(mapInstance, [parseFloat(ev.point.lon), parseFloat(ev.point.lat)], ev.type, ev.point));
  });
  setCurrentHistoryPointMarkers(keyMarkers);

  if (fit && coords.length > 0) {
    const bounds = new maplibregl.LngLatBounds();
    coords.forEach(c => bounds.extend(c));
    const mainM = markers.get(assetUUID);
    if (mainM) bounds.extend(mainM.getLngLat());
    mapInstance.fitBounds(bounds, { padding: 70, maxZoom: 15, duration: 750 });
  }

  const asset = currentFilteredAssets.find(a => a.assetUUID === assetUUID);
  const display = asset ? formatAssetLabel(asset) : assetUUID;
  if (toastText) window.showToast?.(toastText, 'success', display);
}

// Load an absolute [since,until] window (raw=1, tiered hot+archive) and APPEND its
// positional points to the current asset's path — deduped so overlapping windows
// count each point once — then redraw the whole accumulated path. Lets the user
// stack several windows to build up a longer path.
export async function appendHistoryWindow(assetUUID, mapInstance, sinceIso, untilIso) {
  if (!mapInstance || !assetUUID) return 'error';
  // If the path belongs to another asset (or none), start fresh for this one.
  if (routeAssetId !== assetUUID) { clearRoute(mapInstance, { reloadOf: assetUUID }); resetAccum(assetUUID); }
  const seq = ++loadSeq;
  try {
    const url = `api/v1/assets/${encodeURIComponent(assetUUID)}/history?raw=1`
      + `&since=${encodeURIComponent(sinceIso)}&until=${encodeURIComponent(untilIso)}`;
    const res = await window.authenticatedFetch(url);
    if (seq !== loadSeq) return 'ok';   // superseded — a newer action owns the map
    if (!res.ok) { window.showToast?.('History window load failed', 'danger'); return 'error'; }
    const data = await res.json();
    if (seq !== loadSeq) return 'ok';
    const windowPts = (data.history || []).filter(p => p.lat != null && p.lon != null);
    mergeAllRaw(data.history || []);
    if (!windowPts.length) { window.showToast?.('No position data in that window', 'info'); return 'empty'; }

    const before = accumRaw.length;
    mergeAccum(windowPts);
    const added = accumRaw.length - before;
    const merged = simplifyRoute(accumRaw, {});   // client-simplify the merged raw set
    drawRouteFromSimplified(mapInstance, merged, {
      assetUUID, fit: true, toastText: `+${added} new points (${merged.length} on path)`,
    });
    return 'ok';
  } catch (err) {
    console.error('[map] appendHistoryWindow error', err);
    window.showToast?.('Error loading history window', 'danger');
    return 'error';
  }
}

// ── Redraw / camera helpers (no refetch) ────────────────────────────────────

// Redraw the active route from the accumulated points. Used after base-style
// switches (setStyle wipes all custom layers) and when the gradient toggle
// changes. Keeps extra loaded history windows — no network involved.
export function redrawActiveRoute(map, { fit = false } = {}) {
  if (!map || !routeAssetId || accumRaw.length === 0) return;
  const merged = simplifyRoute(accumRaw, {});
  drawRouteFromSimplified(map, merged, { assetUUID: routeAssetId, fit });
}

// One-call restore after a base-style switch: frozen trails first (under),
// then the active route (on top).
export function restoreTrailsAfterStyleChange(map) {
  redrawFrozenTrails(map);
  redrawActiveRoute(map);
}

// Fit the camera to the whole currently drawn path (plus the live marker).
// Returns false when there is no path to fit.
export function fitRouteToView(map) {
  if (!map || currentRoutePoints.length === 0) return false;
  const bounds = new maplibregl.LngLatBounds();
  currentRoutePoints.forEach(p => {
    const lon = parseFloat(p.lon), lat = parseFloat(p.lat);
    if (Number.isFinite(lon) && Number.isFinite(lat)) bounds.extend([lon, lat]);
  });
  if (routeAssetId) {
    const mainM = markers.get(routeAssetId);
    if (mainM) bounds.extend(mainM.getLngLat());
  }
  map.fitBounds(bounds, { padding: 70, maxZoom: 15, duration: 750 });
  return true;
}

// Append the asset's newest live position (from the delta poll) to the drawn
// path, so the trail grows in step with the moving marker. No-op unless this
// asset's route is the one on the map and the fix is newer than the last point.
export function appendLivePosition(map, asset) {
  if (!map || !asset || asset.assetUUID !== routeAssetId) return;
  const lat = parseFloat(asset.lastLat);
  const lon = parseFloat(asset.lastLon);
  if (!Number.isFinite(lat) || !Number.isFinite(lon) || !asset.lastUpdated) return;
  const tMs = new Date(asset.lastUpdated).getTime();
  if (!Number.isFinite(tMs)) return;
  const lastPt = accumRaw[accumRaw.length - 1];
  if (lastPt && tMs <= new Date(lastPt.timestamp).getTime()) return;   // nothing new
  // Sensor-only heartbeats bump lastUpdated without moving the asset — do not
  // pile up identical coordinates on a parked asset.
  if (lastPt &&
      Math.abs(parseFloat(lastPt.lat) - lat) < 1e-7 &&
      Math.abs(parseFloat(lastPt.lon) - lon) < 1e-7) return;

  const p = { lat, lon, timestamp: asset.lastUpdated, accuracy: asset.lastAccuracy };
  mergeAccum([p]);
  mergeAllRaw([p]);
  const merged = simplifyRoute(accumRaw, {});
  drawRouteFromSimplified(map, merged, { assetUUID: routeAssetId, fit: false });
}

// ── Data accessors for the trail-events feature ─────────────────────────────

// All accumulated raw rows (incl. sensor-only heartbeats) for the active asset.
export function getAccumAllRaw() { return accumAllRaw; }

export function getRouteAssetId() { return routeAssetId; }

// Nearest positional point (by time) on the accumulated path — used to place
// sensor events whose own row has no GPS fix. Returns null without a path.
export function nearestRoutePosition(tMs) {
  if (!Number.isFinite(tMs) || accumRaw.length === 0) return null;
  // Binary search over the time-sorted positional accumulation.
  let lo = 0, hi = accumRaw.length - 1;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (new Date(accumRaw[mid].timestamp).getTime() < tMs) lo = mid + 1;
    else hi = mid;
  }
  const cand = [accumRaw[lo], accumRaw[lo - 1]].filter(Boolean);
  let best = null, bestD = Infinity;
  for (const c of cand) {
    const d = Math.abs(new Date(c.timestamp).getTime() - tMs);
    if (d < bestD) { bestD = d; best = c; }
  }
  if (!best) return null;
  const lat = parseFloat(best.lat), lon = parseFloat(best.lon);
  return Number.isFinite(lat) && Number.isFinite(lon) ? { lat, lon } : null;
}
